// ============================================
// ROLC OneROLC - Modal Component
// Version: 3.6.0
// ============================================

import { createElement, $, addClass, removeClass } from '../utils/domHelpers.js';
import { createButton, BUTTON_TYPES } from './Button.js';

export class Modal {
    constructor(options = {}) {
        this.options = {
            title: 'Modal',
            content: '',
            footer: '',
            closable: true,
            onOpen: null,
            onClose: null,
            onConfirm: null,
            confirmText: 'Confirm',
            cancelText: 'Cancel',
            closeOnConfirm: true,
            ...options
        };

        this.isOpen = false;
        this.modal = null;
        this.overlay = null;
        this.modalCloseTimeout = null;

        this._createModal();
    }

    _createModal() {
        this.overlay = createElement('div', 'modal-overlay hidden');
        this.overlay.style.cssText = `
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.6);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            z-index: 2000;
        `;

        this.modal = createElement('div', 'modal');
        this.modal.style.cssText = `
            background: var(--white);
            border-radius: var(--radius);
            width: 100%;
            max-width: 640px;
            max-height: 90vh;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            transform: translateY(20px);
            transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        `;

        this._createHeader();
        this._createBody();
        this._createFooter();

        this.overlay.appendChild(this.modal);
        document.body.appendChild(this.overlay);

        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay && this.options.closable) {
                this.close();
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen && this.options.closable) {
                this.close();
            }
        });
    }

    _createHeader() {
        this.header = createElement('div', 'modal-header');
        this.header.style.cssText = `
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid #e2e8f0;
        `;

        this.titleEl = createElement('h3', 'modal-title', this.options.title);
        this.titleEl.style.cssText = 'font-size:1.2rem;font-weight:700;color:var(--primary);';
        this.header.appendChild(this.titleEl);

        if (this.options.closable) {
            this.closeBtn = createElement('button', 'modal-close', '×');
            this.closeBtn.style.cssText = `
                background: none;
                border: none;
                font-size: 1.8rem;
                color: var(--text-light);
                cursor: pointer;
                line-height: 1;
                padding: 0;
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                transition: background 0.15s;
            `;
            this.closeBtn.addEventListener('click', () => this.close());
            this.closeBtn.addEventListener('mouseenter', () => {
                this.closeBtn.style.background = '#f1f5f9';
            });
            this.closeBtn.addEventListener('mouseleave', () => {
                this.closeBtn.style.background = 'transparent';
            });
            this.header.appendChild(this.closeBtn);
        }

        this.modal.appendChild(this.header);
    }

    _createBody() {
        this.body = createElement('div', 'modal-body');
        this.body.style.cssText = `padding: 24px; overflow-y: auto; flex: 1;`;
        if (this.options.content) {
            this.body.innerHTML = this.options.content;
        }
        this.modal.appendChild(this.body);
    }

    _createFooter() {
        this.footer = createElement('div', 'modal-footer');
        this.footer.style.cssText = `
            padding: 16px 24px;
            border-top: 1px solid #e2e8f0;
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            flex-wrap: wrap;
        `;

        if (this.options.footer) {
            this.footer.innerHTML = this.options.footer;
        } else if (this.options.onConfirm) {
            const cancelBtn = createButton('Cancel', BUTTON_TYPES.SECONDARY, {
                onClick: () => this.close()
            });
            this.footer.appendChild(cancelBtn);

            const confirmBtn = createButton(this.options.confirmText, BUTTON_TYPES.PRIMARY, {
                onClick: () => {
                    if (this.options.onConfirm) {
                        this.options.onConfirm();
                    }
                    if (this.options.closeOnConfirm !== false) {
                        this.close();
                    }
                }
            });
            this.footer.appendChild(confirmBtn);
        }

        this.modal.appendChild(this.footer);
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;

        if (this.modalCloseTimeout) {
            clearTimeout(this.modalCloseTimeout);
            this.modalCloseTimeout = null;
        }

        removeClass(this.overlay, 'hidden');
        document.body.style.overflow = 'hidden';

        this.modal.style.transform = 'translateY(0)';
        this.modal.style.opacity = '1';
        this.modal.style.display = 'flex';

        if (this.options.onOpen) {
            this.options.onOpen();
        }

        const focusable = this.modal.querySelector('button, input, select, textarea');
        if (focusable) {
            setTimeout(() => focusable.focus(), 100);
        }
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;

        this.modal.style.transform = 'translateY(20px)';
        document.body.style.overflow = '';

        this.modalCloseTimeout = setTimeout(() => {
            this.modalCloseTimeout = null;
            addClass(this.overlay, 'hidden');
            if (this.overlay && this.overlay.parentNode) {
                this.overlay.parentNode.removeChild(this.overlay);
            }
        }, 200);

        if (this.options.onClose) {
            this.options.onClose();
        }
    }

    setTitle(title) {
        this.titleEl.textContent = title;
        this.options.title = title;
    }

    setContent(content) {
        this.body.innerHTML = content;
        this.options.content = content;
    }

    setFooter(footer) {
        this.footer.innerHTML = footer;
        this.options.footer = footer;
    }

    showLoading(message = 'Loading...') {
        this.setContent(`
            <div style="text-align:center;padding:20px;">
                <div style="width:40px;height:40px;border:3px solid #e2e8f0;border-top-color:#3b82f6;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto;"></div>
                <p style="margin-top:12px;color:#6b7280;">${message}</p>
                <style>
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                </style>
            </div>
        `);
    }

    showError(message) {
        this.setContent(`
            <div style="text-align:center;padding:20px;">
                <div style="font-size:3rem;margin-bottom:12px;">❌</div>
                <p style="color:#dc2626;">${message}</p>
            </div>
        `);
    }

    showSuccess(message) {
        this.setContent(`
            <div style="text-align:center;padding:20px;">
                <div style="font-size:3rem;margin-bottom:12px;">✅</div>
                <p style="color:#16a34a;">${message}</p>
            </div>
        `);
    }

    destroy() {
        this.close();
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
    }
}

export function showConfirm(title, message, options = {}) {
    return new Promise((resolve) => {
        const modal = new Modal({
            title,
            content: `<p>${message}</p>`,
            confirmText: options.confirmText || 'Yes',
            cancelText: options.cancelText || 'Cancel',
            closeOnConfirm: false,
            onConfirm: () => {
                resolve(true);
                modal.close();
            },
            onClose: () => {
                resolve(false);
            }
        });
        modal.open();
    });
}

export function showAlert(title, message, options = {}) {
    return new Promise((resolve) => {
        const modal = new Modal({
            title,
            content: `<p>${message}</p>`,
            confirmText: options.confirmText || 'OK',
            cancelText: null,
            closeOnConfirm: false,
            onConfirm: () => {
                resolve(true);
                modal.close();
            },
            onClose: () => {
                resolve(false);
            }
        });
        modal.open();
    });
}


